import { FormEvent, useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useAuthStore, selectIsAuthenticated } from '@/stores/authStore'
import { startGoogleLogin, startFacebookLogin } from '@/services/api'
import { useAuthProviders } from '@/hooks/useAuthProviders'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { ConsentLabel } from '@/components/low/ConsentLabel'
import { BrandLogo } from '@/components/low/BrandLogo'

export function RegisterPage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const returnUrl = searchParams.get('returnUrl') || '/'

    const isAuthenticated = useAuthStore(selectIsAuthenticated)
    const isInitialized = useAuthStore((s) => s.isInitialized)
    const { register, isLoading } = useAuthStore()

    const [email, setEmail] = useState('')
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [acceptTerms, setAcceptTerms] = useState(false)
    const [termsError, setTermsError] = useState<string | null>(null)
    const [serverError, setServerError] = useState<string | null>(null)
    const [fieldErrors, setFieldErrors] = useState<Record<'username' | 'password' | 'confirmPassword', string | null>>({
        username: null,
        password: null,
        confirmPassword: null,
    })
    // Track which fields have been interacted with (to avoid showing errors before user typed)
    const [touched, setTouched] = useState<Record<'username' | 'password' | 'confirmPassword', boolean>>({
        username: false,
        password: false,
        confirmPassword: false,
    })

    const providers = useAuthProviders()
    const hasSocialLogin = providers.google || providers.facebook

    // Redirect if already logged in
    useEffect(() => {
        if (isInitialized && isAuthenticated) {
            navigate(returnUrl, { replace: true })
        }
    }, [isInitialized, isAuthenticated, navigate, returnUrl])

    const validateField = (
        field: 'username' | 'password' | 'confirmPassword',
        value: string,
        currentPassword?: string,
    ): string | null => {
        if (field === 'username') {
            if (value.length < 3 || value.length > 50) return t('auth.usernameLengthError')
        }
        if (field === 'password') {
            if (value.length < 8) return t('auth.passwordLengthError')
        }
        if (field === 'confirmPassword') {
            const pwd = currentPassword ?? password
            if (value !== pwd) return t('auth.passwordMismatchError')
        }
        return null
    }

    const handleFieldChange = (
        field: 'username' | 'password' | 'confirmPassword',
        value: string,
    ) => {
        if (field === 'username') setUsername(value)
        if (field === 'password') setPassword(value)
        if (field === 'confirmPassword') setConfirmPassword(value)

        if (touched[field]) {
            // Re-validate this field immediately
            const err = field === 'confirmPassword'
                ? validateField(field, value, field === 'confirmPassword' ? password : undefined)
                : validateField(field, value)
            setFieldErrors((prev) => ({ ...prev, [field]: err }))

            // When password changes, also re-validate confirmPassword if touched
            if (field === 'password' && touched.confirmPassword) {
                const confirmErr = validateField('confirmPassword', confirmPassword, value)
                setFieldErrors((prev) => ({ ...prev, confirmPassword: confirmErr }))
            }
        }
    }

    const handleBlur = (field: 'username' | 'password' | 'confirmPassword') => {
        setTouched((prev) => ({ ...prev, [field]: true }))
        const value = field === 'username' ? username : field === 'password' ? password : confirmPassword
        const err = field === 'confirmPassword'
            ? validateField(field, value, password)
            : validateField(field, value)
        setFieldErrors((prev) => ({ ...prev, [field]: err }))
    }

    // Defensive full validation at submit time (keeps all errors visible at once)
    const validateAll = (): boolean => {
        const usernameErr = validateField('username', username)
        const passwordErr = validateField('password', password)
        const confirmErr = validateField('confirmPassword', confirmPassword, password)
        setFieldErrors({ username: usernameErr, password: passwordErr, confirmPassword: confirmErr })
        setTouched({ username: true, password: true, confirmPassword: true })
        const termsErr = acceptTerms ? null : t('auth.termsRequiredError')
        setTermsError(termsErr)
        return !usernameErr && !passwordErr && !confirmErr && !termsErr
    }

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()

        if (!validateAll()) return

        setServerError(null)
        try {
            await register(email, username, password, acceptTerms)
            navigate(returnUrl, { replace: true })
        } catch (err) {
            const msg = (err as { message?: string }).message ?? t('auth.registerFailed')
            setServerError(msg)
            toast.error(msg)
        }
    }

    const handleGoogleLogin = () => {
        sessionStorage.setItem('auth-return-url', returnUrl)
        startGoogleLogin()
    }

    const handleFacebookLogin = () => {
        sessionStorage.setItem('auth-return-url', returnUrl)
        startFacebookLogin()
    }

    return (
        <div className="min-h-screen bg-background text-foreground">
            <div className="max-w-2xl mx-auto px-4 py-8 md:px-6 md:py-12">
                {/* Header */}
                <header className="mb-8">
                    <Link to="/" className="flex items-center gap-3 hover:opacity-80 w-fit mb-6">
                        <BrandLogo className="h-8 md:h-9" />
                    </Link>
                    <h1 className="text-xl font-display font-semibold text-foreground">{t('auth.register')}</h1>
                </header>

                {/* Content */}
                <main>
                    <Card>
                        <CardHeader className="mb-0" />
                        <CardContent className="pt-6">
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Email */}
                                <div className="space-y-2">
                                    <Label htmlFor="email">{t('auth.emailLabel')}</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={isLoading}
                                        placeholder={t('auth.emailPlaceholder')}
                                        autoComplete="email"
                                        required
                                        className="min-h-[44px]"
                                    />
                                </div>

                                {/* Username */}
                                <div className="space-y-2">
                                    <Label htmlFor="username">{t('auth.usernameLabel')}</Label>
                                    <Input
                                        id="username"
                                        type="text"
                                        value={username}
                                        onChange={(e) => handleFieldChange('username', e.target.value)}
                                        onBlur={() => handleBlur('username')}
                                        disabled={isLoading}
                                        placeholder={t('auth.usernamePlaceholder')}
                                        autoComplete="username"
                                        minLength={3}
                                        maxLength={50}
                                        required
                                        className="min-h-[44px]"
                                    />
                                    {fieldErrors.username
                                        ? <p className="text-destructive text-sm">{fieldErrors.username}</p>
                                        : <p className="text-muted-foreground text-sm">{t('auth.usernameHint')}</p>
                                    }
                                </div>

                                {/* Password */}
                                <div className="space-y-2">
                                    <Label htmlFor="password">{t('auth.passwordLabel')}</Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        value={password}
                                        onChange={(e) => handleFieldChange('password', e.target.value)}
                                        onBlur={() => handleBlur('password')}
                                        disabled={isLoading}
                                        placeholder="••••••••"
                                        autoComplete="new-password"
                                        minLength={8}
                                        required
                                        className="min-h-[44px]"
                                    />
                                    {fieldErrors.password
                                        ? <p className="text-destructive text-sm">{fieldErrors.password}</p>
                                        : <p className="text-muted-foreground text-sm">{t('auth.passwordHint')}</p>
                                    }
                                </div>

                                {/* Confirm Password */}
                                <div className="space-y-2">
                                    <Label htmlFor="confirm-password">{t('auth.confirmPasswordLabel')}</Label>
                                    <Input
                                        id="confirm-password"
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => handleFieldChange('confirmPassword', e.target.value)}
                                        onBlur={() => handleBlur('confirmPassword')}
                                        disabled={isLoading}
                                        placeholder="••••••••"
                                        autoComplete="new-password"
                                        required
                                        className="min-h-[44px]"
                                    />
                                    {fieldErrors.confirmPassword && (
                                        <p className="text-destructive text-sm">{fieldErrors.confirmPassword}</p>
                                    )}
                                </div>

                                {/* Terms acceptance */}
                                <div className="space-y-2">
                                    <div className="flex items-start gap-2">
                                        <Checkbox
                                            id="accept-terms"
                                            data-testid="accept-terms"
                                            checked={acceptTerms}
                                            onChange={(e) => {
                                                setAcceptTerms(e.target.checked)
                                                if (e.target.checked) setTermsError(null)
                                            }}
                                            disabled={isLoading}
                                            className="mt-1"
                                        />
                                        <Label htmlFor="accept-terms" className="text-sm font-normal leading-snug">
                                            <ConsentLabel />
                                        </Label>
                                    </div>
                                    {termsError && (
                                        <p data-testid="accept-terms-error" className="text-destructive text-sm">
                                            {termsError}
                                        </p>
                                    )}
                                </div>

                                {/* Server error */}
                                {serverError && (
                                    <p
                                        data-testid="register-error"
                                        className="text-destructive text-sm text-center"
                                    >
                                        {serverError}
                                    </p>
                                )}

                                {/* Submit */}
                                <Button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full min-h-[44px]"
                                >
                                    {isLoading ? t('auth.creatingAccount') : t('auth.register')}
                                </Button>

                                {hasSocialLogin && (
                                    <>
                                        {/* OAuth Divider */}
                                        <div className="flex items-center gap-4">
                                            <div className="h-px flex-grow bg-border" />
                                            <span className="text-xs text-muted-foreground">{t('auth.or')}</span>
                                            <div className="h-px flex-grow bg-border" />
                                        </div>

                                        {/* OAuth Buttons */}
                                        <div className="space-y-3">
                                            {providers.google && (
                                                <Button
                                                    type="button"
                                                    onClick={handleGoogleLogin}
                                                    disabled={isLoading}
                                                    variant="outline"
                                                    className="w-full min-h-[44px]"
                                                >
                                                    {t('auth.signUpWithGoogle')}
                                                </Button>
                                            )}
                                            {providers.facebook && (
                                                <Button
                                                    type="button"
                                                    onClick={handleFacebookLogin}
                                                    disabled={isLoading}
                                                    variant="outline"
                                                    className="w-full min-h-[44px]"
                                                >
                                                    {t('auth.signUpWithFacebook')}
                                                </Button>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* Login link */}
                                <p className="text-center text-sm text-muted-foreground">
                                    {t('auth.haveAccount')}{' '}
                                    <Link
                                        to={`/login${returnUrl !== '/' ? `?returnUrl=${encodeURIComponent(returnUrl)}` : ''}`}
                                        className="text-primary hover:text-primary-hover transition-colors font-semibold"
                                    >
                                        {t('auth.login')}
                                    </Link>
                                </p>
                            </form>
                        </CardContent>
                    </Card>
                </main>
            </div>
        </div>
    )
}
